import { ref, computed } from 'vue'

// Currency mapping
const currencyMap = {
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  CHF: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound' },
  THB: { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen' }
}

// Global state
const systemPreferences = ref(null)
const isLoading = ref(false)
const error = ref(null)

export const useCurrency = () => {
  // Fetch system preferences from API
  const fetchSystemPreferences = async () => {
    if (systemPreferences.value) {
      return systemPreferences.value
    }

    isLoading.value = true
    error.value = null

    try {
      const apiUrl = ''
      const response = await fetch(`${apiUrl}/api/settings/system`)
      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to fetch system preferences')
      }

      systemPreferences.value = result.data
      return result.data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred'
      console.error('Error fetching system preferences:', err)
      
      // Return default values on error
      const defaultPreferences = {
        currency: 'EUR',
        language: 'en',
        timezone: 'Europe/Berlin',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '24h'
      }
      
      systemPreferences.value = defaultPreferences
      return defaultPreferences
    } finally {
      isLoading.value = false
    }
  }

  // Get currency info
  const currencyInfo = computed(() => {
    const currency = systemPreferences.value?.currency || 'EUR'
    return currencyMap[currency] || currencyMap['EUR']
  })

  // Get currency symbol
  const currencySymbol = computed(() => {
    return currencyInfo.value.symbol
  })

  // Get currency code
  const currencyCode = computed(() => {
    return currencyInfo.value.code
  })

  // Format currency value
  const formatCurrency = (value) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    
    if (isNaN(numValue)) {
      return `${currencySymbol.value}0`
    }

    const currency = currencyCode.value
    
    try {
      // Use Intl.NumberFormat for proper currency formatting
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(numValue)
    } catch (err) {
      // Fallback formatting if Intl fails
      return `${currencySymbol.value}${numValue.toLocaleString()}`
    }
  }

  // Format currency value with custom symbol placement
  const formatCurrencyWithSymbol = (value) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    
    if (isNaN(numValue)) {
      return `${currencySymbol.value}0`
    }

    return `${currencySymbol.value}${numValue.toLocaleString()}`
  }

  return {
    // State
    systemPreferences: computed(() => systemPreferences.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    
    // Currency info
    currencyInfo,
    currencySymbol,
    currencyCode,
    
    // Methods
    fetchSystemPreferences,
    formatCurrency,
    formatCurrencyWithSymbol,
    
    // Available currencies
    availableCurrencies: Object.values(currencyMap)
  }
}